import { useState } from "react";
import api from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

function PasswordField({ id, label, value, onChange, autoComplete, testId, hint }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs" htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 rounded-lg pr-10"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Şifreyi gizle" : "Şifreyi göster"}
          aria-pressed={visible}
          tabIndex={-1}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-800 focus:outline-none"
          data-testid={`${testId}-toggle`}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {hint ? <p className="text-[11px] text-slate-500">{hint}</p> : null}
    </div>
  );
}

export default function AdminProfile() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) {
      toast.error("Lütfen tüm alanları doldurun.");
      return;
    }
    if (next.length < 8) {
      toast.error("Yeni şifre en az 8 karakter olmalı.");
      return;
    }
    if (next !== confirm) {
      toast.error("Yeni şifre ile tekrar alanı eşleşmiyor.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/admin/profile/password", {
        current_password: current,
        new_password: next,
      });
      reset();
      toast.success("Şifreniz başarıyla değiştirildi.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Şifre değiştirilemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-profile-page">
      <PageHeader
        title="Profil"
        subtitle="Platform yöneticisi hesap ayarları"
      />

      <div className="max-w-xl">
        <SectionCard title="Şifre Değiştir">
          <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="admin-profile-password-form">
            <PasswordField
              id="admin-current-password"
              label="Mevcut Şifre"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              testId="admin-current-password-input"
            />
            <PasswordField
              id="admin-new-password"
              label="Yeni Şifre"
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              testId="admin-new-password-input"
              hint="En az 8 karakter olmalı."
            />
            <PasswordField
              id="admin-confirm-password"
              label="Yeni Şifre Tekrar"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              testId="admin-confirm-password-input"
            />

            <div className="pt-2 flex justify-end">
              <Button
                type="submit"
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800 rounded-lg"
                data-testid="admin-change-password-submit"
              >
                {saving ? "Kaydediliyor..." : "Şifreyi Değiştir"}
              </Button>
            </div>
          </form>
        </SectionCard>
      </div>
    </div>
  );
}
